<script setup lang="ts">
import { computed } from 'vue';

import TablePageFooter from '@/components/footers/TablePageFooter.vue';
import ExportAsCsvIconBtn from '@/components/iconBtns/ExportAsCsvIconBtn.vue';
import SmsHistorySearchForm from '@/components/searchForms/SmsHistorySearchForm.vue';
import SmsHistoryTable from '@/components/tables/SmsHistoryTable.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import TablePageShadowWrapper from '@/components/wrappers/TablePageShadowWrapper.vue';
import useCommonRouteQuery from '@/composables/routeQueries/useCommonRouteQuery';
import useCurrentDateString from '@/composables/useCurrentDateString';
import useDownloadCsv from '@/composables/useDownloadCsv';
import useSmsHistoryView from '@/composables/views/useSmsHistoryView';
import { defCurPage, defPageSize } from '@/constants/routeQuery';

const { getLastSevenDaysRange } = useCurrentDateString();

const { queryCurPage, queryPageSize, queryDriver, queryDates } =
  useCommonRouteQuery({
    queryCurPage: defCurPage,
    queryPageSize: defPageSize,
    queryDates: getLastSevenDaysRange(),
  });

const queryFromDate = computed(() => queryDates.value?.[0]);
const queryToDate = computed(() => queryDates.value?.[1]);

const { search, initialData, isFetchingData, smsHistoryData } =
  useSmsHistoryView(
    queryCurPage,
    queryPageSize,
    queryFromDate,
    queryToDate,
    queryDriver,
  );

const { downloadCsv, isDownloadingCSV } = useDownloadCsv();

function downloadCsvSMSHistory(): void {
  const items = smsHistoryData.value.items;
  if (items.length === 0) return;
  downloadCsv(items, 'SMS History Report');
}
</script>

<template>
  <div :class="$style.smsHistoryView" data-testid="smsHistoryView">
    <PageContentWrapper>
      <FormWrapper :class="$style.formWrapper">
        <SmsHistorySearchForm
          :initial-data="initialData"
          :loading="isFetchingData"
          :class="$style.form"
          @submit="search"
        />
      </FormWrapper>

      <TablePageShadowWrapper :class="$style.tablePageShadowWrapper">
        <SmsHistoryTable
          :data="smsHistoryData.items || []"
          :loading="isFetchingData"
        />
        <TablePageFooter
          v-if="smsHistoryData.items?.length"
          :cur-page="queryCurPage"
          :page-size="queryPageSize"
          :total="smsHistoryData.total"
        >
          <ExportAsCsvIconBtn
            :disabled="isFetchingData || !smsHistoryData.items?.length"
            :loading="isDownloadingCSV"
            @click="downloadCsvSMSHistory()"
          />
        </TablePageFooter>
      </TablePageShadowWrapper>
    </PageContentWrapper>
  </div>
</template>

<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.smsHistoryView {
}

.formWrapper {
  padding: 2.4rem 1.5rem 0.5rem 1.5rem;
  max-width: variables.$tableWrapperWidth;
  margin: 2rem 0;
}

.tablePageShadowWrapper {
  max-width: variables.$tableWrapperWidth;
}
</style>
